import { useCallback, useEffect, useId, useState, type KeyboardEvent, type ReactElement } from "react";
import { UserPlus } from "lucide-react";
import { space } from "@tokens/index";
import { Button, Text, TextInput } from "@primitives/index";
import { UserResultRow } from "@compositions/index";
import { Field } from "../admin-ui";
import type { UserSearchResult } from "../queues";

/**
 * UserSearchPicker: an inline HR user search with one add action per
 * result. Results render under the input as part of the page, not a
 * floating panel. Keyboard: ArrowUp/ArrowDown move the highlight, Enter
 * adds the highlighted (or first) result, Escape clears the search.
 * Rows share their grammar with the queue-admin `UserPicker`.
 */

interface UserSearchPickerProps {
    readonly label: string;
    readonly placeholder: string;
    readonly searchTerm: string;
    readonly onSearchTermChange: (term: string) => void;
    readonly results: readonly UserSearchResult[];
    readonly isFetching: boolean;
    readonly excludedIds: ReadonlySet<string>;
    readonly onAdd: (user: UserSearchResult) => void;
    readonly isAdding: boolean;
    readonly addLabel: string;
    readonly excludedLabel: string;
}

export function UserSearchPicker({
    label,
    placeholder,
    searchTerm,
    onSearchTermChange,
    results,
    isFetching,
    excludedIds,
    onAdd,
    isAdding,
    addLabel,
    excludedLabel,
}: UserSearchPickerProps): ReactElement {
    const listId = useId();
    const [activeIndex, setActiveIndex] = useState(-1);
    const showEmpty = searchTerm.length > 0 && !isFetching && results.length === 0;
    const hasResults = results.length > 0;

    // A new result set invalidates the old highlight.
    useEffect(() => {
        setActiveIndex(-1);
    }, [results]);

    const canAdd = useCallback(
        (user: UserSearchResult | undefined): user is UserSearchResult =>
            user !== undefined && !isAdding && !excludedIds.has(user.id),
        [isAdding, excludedIds],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Escape") {
                if (searchTerm.length === 0) return;
                event.preventDefault();
                event.stopPropagation();
                onSearchTermChange("");
                setActiveIndex(-1);
            } else if (event.key === "ArrowDown") {
                if (!hasResults) return;
                event.preventDefault();
                setActiveIndex((i) => (i + 1) % results.length);
            } else if (event.key === "ArrowUp") {
                if (!hasResults) return;
                event.preventDefault();
                setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
            } else if (event.key === "Enter") {
                if (!hasResults) return;
                event.preventDefault();
                const user = results[activeIndex] ?? results[0];
                if (canAdd(user)) onAdd(user);
            }
        },
        [searchTerm, hasResults, results, activeIndex, canAdd, onAdd, onSearchTermChange],
    );

    return (
        <div style={{ display: "grid", gap: space[2], minWidth: 0 }}>
            <Field label={label}>
                {(id) => (
                    <TextInput
                        id={id}
                        type="search"
                        autoComplete="off"
                        placeholder={placeholder}
                        fullWidth
                        value={searchTerm}
                        role="combobox"
                        aria-expanded={hasResults}
                        aria-controls={listId}
                        aria-autocomplete="list"
                        aria-activedescendant={
                            activeIndex >= 0 ? `${listId}-${String(activeIndex)}` : undefined
                        }
                        onChange={(e) => onSearchTermChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                )}
            </Field>
            {isFetching && (
                <div role="status">
                    <Text role="caption" tone="muted">
                        Searching…
                    </Text>
                </div>
            )}
            {showEmpty && (
                <Text role="caption" tone="muted">
                    No results.
                </Text>
            )}
            {hasResults && (
                <div id={listId} role="listbox" aria-label={`${label} results`}>
                    {results.map((user, index) => {
                        const excluded = excludedIds.has(user.id);
                        return (
                            <UserResultRow
                                key={user.id}
                                id={`${listId}-${String(index)}`}
                                role="option"
                                aria-selected={index === activeIndex}
                                aria-disabled={excluded || undefined}
                                user={user}
                                active={index === activeIndex}
                                style={{ cursor: "default" }}
                                onMouseEnter={() => setActiveIndex(index)}
                                trailing={
                                    <Button
                                        type="button"
                                        variant="soft"
                                        size="sm"
                                        onClick={() => onAdd(user)}
                                        disabled={isAdding || excluded}
                                    >
                                        <UserPlus size={14} strokeWidth={2.25} aria-hidden="true" />
                                        {excluded ? excludedLabel : addLabel}
                                    </Button>
                                }
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}
